/**
 * `orange-blocked-state` — component-spec.md §8. The S-20 pattern.
 *
 * Fourteen situations can stop or divert this flow. This is the one pattern used
 * for all of them, rendered **in place** wherever the flow stops — S-20 itself is
 * labelled "TIPAR · NU ESTE UN SINGUR ECRAN" in the prototype and is not a screen.
 * The catalogue of all fourteen lives behind the dev route.
 *
 * ### How the three outcomes are told apart
 *
 * By fill, border weight, marker shape and a word — never by coloured type, since
 * none of the status hues clears AA as text. From the prototype's `OUTCOME` table:
 *
 *   BLOCKED     → `--surface-inverse` bar, `--surface` word, 2px `--ink` border, diamond
 *   DEGRADED    → `--grey-300` bar, `--ink` word, 1px hairline border, square
 *   REDIRECTED  → `--brand` bar, `--ink` word, 2px `--ink` border, triangle
 *
 * `component-spec.md` §8 describes REDIRECTED as a `--warning` strip; the prototype
 * uses `--brand`. Prototype wins on appearance (CLAUDE.md), and both are fills with
 * `--ink` text, so the contrast argument is unaffected — 7.99:1 either way.
 *
 * This has to read as *different in kind* from a recoverable field error, which is
 * an 11px `--danger` chip and a line of text under an input with no panel and no
 * bar. Here the whole panel is replaced: full-width bar, 2px border, a heading.
 * A customer who can fix something and a customer who cannot must not be looking
 * at the same treatment.
 *
 * ### Eleven of the fourteen outcomes are invented
 *
 * And the sources cannot agree on how many. `schema.graphql` says the material
 * "assigns an outcome to only two of the fourteen triggers" without naming them;
 * the prototype's matrix marks **three** as `assigned` (not authenticated,
 * subscription cap, device out of stock) and its own footnote says three. The
 * prototype is the only source that names any, so its three are used and the
 * discrepancy is recorded in `docs/SCREEN-MAP.md` rather than averaged away.
 *
 * Every other default outcome in `TRIGGERS` below carries an `unspecified()`
 * marker, so all eleven appear in `docs/OPEN-QUESTIONS.md` and the dev panel can
 * switch any of them to the other two without a code change.
 *
 * ### Where the resolved outcome comes from
 *
 * Four layers, in order: the dev switch, then an *assigned* outcome, then the
 * caller's, then the table's default. The second layer exists because the POC has
 * two independent sources of default and they disagree on five triggers — see the
 * comment on `outcome` for the table and the reasoning.
 *
 * ### Copy
 *
 * All Romanian below is the prototype's and is `[PLACEHOLDER]` in the sense
 * CLAUDE.md means — Orange supplies real customer-facing copy. Two triggers say so
 * in the copy itself, because the *rule* is undefined and not just the wording.
 *
 * One line is deliberately *not* carried over from the prototype. It prints
 * "Continuare posibilă, cu ofertele de consumer." under every trigger it draws as
 * DEGRADED, because that page is a catalogue and the sentence is the outcome's
 * definition. In the product it would be a promise, and it is true of exactly one
 * trigger. It is per-trigger here — see `degradedNote`.
 */
import {
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import type { BlockOutcome, BlockReason } from '../../../generated/graphql';
import { BlockOutcomes } from '../../core/dev/block-outcomes';
import { unspecified } from '../../core/domain/unspecified';
import { ButtonComponent } from '../button/button.component';

export interface BlockAction {
  readonly id: string;
  readonly label: string;
  /** One per block at most, matching the prototype. */
  readonly primary?: boolean;
}

interface Trigger {
  readonly title: string;
  readonly body: string;
  readonly actions: readonly BlockAction[];
  /** Short code for the support reference line. */
  readonly code: string;
  /**
   * The outcome shown when the caller does not override it. Marked
   * `unspecified()` for the eleven the sources leave open.
   */
  readonly outcome: BlockOutcome;
  /** True only where a source states what the customer sees. Three of fourteen. */
  readonly outcomeAssigned: boolean;
  /**
   * What the degradation *is*, for the one trigger where a source says so.
   *
   * The prototype prints `"Continuare posibilă, cu ofertele de consumer."` under
   * every trigger it draws as DEGRADED ([pre2post-prototype_f.html:3368]), because
   * that page is a catalogue and the line is the *definition* of the outcome —
   * `OUTCOME.degraded.lead` at :3241 says the same thing in the third person.
   * Carrying it into the product pattern would make it a promise, and it is only
   * true for `NOT_AUTHENTICATED`:
   *
   *   - `NOT_AUTHENTICATED` → consumer offers. Stated, and `outcomeAssigned`.
   *   - `DEVICE_OUT_OF_STOCK` → the order continues without the handset, which the
   *     trigger's own body and its two actions already say. Also assigned, so
   *     nothing more is needed and `null` here means "the body covers it".
   *   - `FEATURE_DISABLED` → its body says the *business* plans stay visible, which
   *     the consumer-offers line directly contradicts. And DEGRADED is this POC's
   *     guess for it, not a source's (Q-12).
   *
   * For the other eleven the question does not arise until the dev switch forces
   * DEGRADED on them, and then the honest answer is that nobody has described what
   * a degraded `COMPANY_NOT_ACTIVE` would even look like. So: `null` plus
   * `outcomeAssigned: false` renders a marked placeholder rather than a sentence
   * about consumer offers that no source supports.
   */
  readonly degradedNote: string | null;
}

/**
 * The fourteen triggers, in the prototype's order. `where` and the decision-state
 * chips are documentation and live in the dev catalogue, not here.
 */
const TRIGGERS: Readonly<Record<BlockReason, Trigger>> = {
  FEATURE_DISABLED: {
    code: 'FLAG',
    title: $localize`:@@block.flag.title:Migrarea cartelei nu este disponibilă momentan`,
    body: $localize`:@@block.flag.body:Această opțiune nu este activă acum. Poți vedea în continuare abonamentele business.`,
    actions: [
      {
        id: 'view-business-plans',
        label: $localize`:@@block.act.viewBusinessPlans:Vezi abonamentele business`,
        primary: true,
      },
      {
        id: 'contact-business',
        label: $localize`:@@block.act.contactBusiness:Contactează echipa business`,
      },
    ],
    // No BRD question covers the feature-flag block at all — the schema writes
    // `q: "none"` for this class of gap. Filed under Q-12 (eligibility failure
    // behaviour) so it appears in the generated list instead of vanishing.
    outcome: unspecified<BlockOutcome>(
      'DEGRADED',
      'Q-12',
      'What a customer sees when the migration feature flag is off. No source assigns an outcome and no BRD question covers it.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  NOT_AUTHENTICATED: {
    code: 'ANON',
    title: $localize`:@@block.anon.title:Autentifică-te ca să vezi ofertele firmei tale`,
    body: $localize`:@@block.anon.body:Fără autentificare îți arătăm ofertele pentru persoane fizice. Migrarea pe abonament business se face din contul firmei.`,
    actions: [
      { id: 'sign-in', label: $localize`:@@block.act.signIn:Autentifică-te`, primary: true },
      {
        id: 'view-consumer-offers',
        label: $localize`:@@block.act.viewConsumerOffers:Vezi ofertele pentru persoane fizice`,
      },
    ],
    // Assigned in the sources: an anonymous visitor sees consumer offers.
    outcome: 'DEGRADED',
    outcomeAssigned: true,
    // The one trigger the prototype's degraded line is actually true of.
    degradedNote: $localize`:@@block.anon.degraded:Continuare posibilă, cu ofertele de consumer.`,
  },

  NOT_ADMINISTRATOR: {
    code: 'NOTADMIN',
    title: $localize`:@@block.notadmin.title:Nu ai drepturi de administrator pe acest cont`,
    body: $localize`:@@block.notadmin.body:Comenzile de migrare se plasează de administratorul contului firmei. Te putem pune în legătură cu echipa business.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
      {
        id: 'who-is-admin',
        label: $localize`:@@block.act.whoIsAdmin:Vezi cine este administrator`,
      },
    ],
    outcome: unspecified<BlockOutcome>(
      'REDIRECTED',
      'Q-15',
      'What a non-administrator on an otherwise valid business account sees.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  NO_ACTIVE_OCN: {
    code: 'OCN',
    title: $localize`:@@block.ocn.title:Contul firmei nu este activ pentru comenzi online`,
    body: $localize`:@@block.ocn.body:Nu găsim un cont Orange activ pe care să plasăm comanda. Un coleg de la Orange poate verifica situația contului.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
    ],
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-15',
      'What a customer with no active OCN sees.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  COMPANY_NOT_ACTIVE: {
    code: 'COMPANY',
    title: $localize`:@@block.company.title:Firma nu figurează ca activă`,
    body: $localize`:@@block.company.body:Comanda nu poate fi plasată pentru o firmă inactivă.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
    ],
    // Q-12 covers the rule as well as the outcome: "active" is itself undefined.
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-12',
      'What a customer whose company is not active sees. The definition of "active" is also open.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  CUI_VALIDATION_FAILED: {
    code: 'CUIFAIL',
    title: $localize`:@@block.cuifail.title:Nu am putut confirma datele firmei`,
    body: $localize`:@@block.cuifail.body:Verificarea codului fiscal nu a trecut. Datele firmei sunt preluate automat din contul tău, deci nu le poți corecta de aici.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
    ],
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-03',
      'What a customer sees when CUI validation returns a negative result.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  CUI_SERVICE_UNAVAILABLE: {
    code: 'CUIDOWN',
    title: $localize`:@@block.cuidown.title:Nu putem verifica datele firmei chiar acum`,
    body: $localize`:@@block.cuidown.body:Un serviciu de care depindem nu răspunde. Încearcă din nou peste câteva minute.`,
    actions: [
      { id: 'retry', label: $localize`:@@block.act.retry:Încearcă din nou`, primary: true },
      {
        id: 'contact-business',
        label: $localize`:@@block.act.contactBusiness:Contactează echipa business`,
      },
    ],
    // The open question here is sharper than the wording: whether an unreachable
    // registry is a hard block or the flow proceeds without the check.
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-03',
      'Whether an unreachable CUI service is a hard block or the order proceeds without the check.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  INTERNAL_CHECK_FAILED: {
    code: 'ELIG',
    title: $localize`:@@block.elig.title:Comanda nu poate fi finalizată online`,
    body: $localize`:@@block.elig.body:Pentru situația firmei tale este nevoie de o discuție cu un coleg din echipa business.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
    ],
    outcome: unspecified<BlockOutcome>(
      'REDIRECTED',
      'Q-07',
      'What a customer sees when the internal eligibility check fails. The system performing the check is itself unnamed.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  SUBSCRIPTION_CAP_REACHED: {
    code: 'CAP',
    title: $localize`:@@block.cap.title:Ai atins limita de abonamente din contract`,
    body: $localize`:@@block.cap.body:Contractul curent are toate cele 10 abonamente ocupate. Extinderea se face prin echipa business.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
      { id: 'view-my-orders', label: $localize`:@@block.act.viewMyOrders:Vezi comenzile mele` },
    ],
    // Assigned in the sources. The cap's *value* is not — that is Q-13, and it
    // lives in the mock BFF's CART config, not here.
    outcome: 'BLOCKED',
    outcomeAssigned: true,
    // BLOCKED is the assigned outcome, so a degraded cap is a dev-switch fiction
    // and nothing describes it. The placeholder says so when it is forced.
    degradedNote: null,
  },

  NO_ELIGIBLE_OFFERS: {
    code: 'NOOFFER',
    title: $localize`:@@block.nooffer.title:Nu găsim abonamente disponibile pentru firma ta`,
    body: $localize`:@@block.nooffer.body:Contractul firmei nu are momentan oferte pe care le poți alege singur din eShop.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
    ],
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-04',
      'What a customer with no eligible offers sees.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  MSISDN_NOT_ELIGIBLE: {
    code: 'MSISDN',
    title: $localize`:@@block.msisdn.title:Numărul introdus nu poate fi mutat pe abonament`,
    // The copy says it is a placeholder because the *rule* is undefined, not just
    // the wording. Softening this to something plausible is the exact failure
    // CLAUDE.md warns about.
    body: $localize`:@@block.msisdn.body:[MESAJ PLACEHOLDER] Regula după care se respinge numărul și textul afișat clientului nu sunt definite.`,
    actions: [
      {
        id: 'try-another-number',
        label: $localize`:@@block.act.tryAnotherNumber:Încearcă alt număr`,
        primary: true,
      },
      { id: 'get-help', label: $localize`:@@block.act.getHelp:Cere ajutor` },
    ],
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-16',
      'What a customer sees when a number cannot be migrated. The rejection rule itself is undefined.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  OTP_FAILED_TERMINALLY: {
    code: 'OTP',
    title: $localize`:@@block.otp.title:Nu am putut confirma numărul`,
    body: $localize`:@@block.otp.body:Codul de verificare nu a fost introdus corect. [PLACEHOLDER] Nu este definit după cât timp se poate relua sau dacă există o rută asistată.`,
    actions: [
      { id: 'get-help', label: $localize`:@@block.act.getHelp:Cere ajutor`, primary: true },
    ],
    outcome: unspecified<BlockOutcome>(
      'BLOCKED',
      'Q-24',
      'What happens after OTP attempts are exhausted — whether there is a lockout period, a retry, or an assisted route.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },

  DEVICE_OUT_OF_STOCK: {
    code: 'STOCK',
    title: $localize`:@@block.stock.title:Telefonul ales nu mai este disponibil`,
    body: $localize`:@@block.stock.body:Poți alege alt telefon sau poți continua migrarea doar cu abonamentul.`,
    actions: [
      {
        id: 'choose-another-device',
        label: $localize`:@@block.act.chooseAnotherDevice:Alege alt telefon`,
        primary: true,
      },
      {
        id: 'continue-sim-only',
        label: $localize`:@@block.act.continueSimOnly:Continuă doar cu abonamentul`,
      },
    ],
    // Assigned in the sources: out of stock degrades to SIM-only rather than
    // stopping the order. When the reservation expires is Q-33, not this.
    outcome: 'DEGRADED',
    outcomeAssigned: true,
    // The degradation is the body and the second action: continue without the
    // handset. Nothing to add, and the prototype's consumer-offers line would be
    // wrong here — this customer is signed in and staying on business pricing.
    degradedNote: null,
  },

  INSTALMENTS_DECLINED: {
    code: 'RATE',
    title: $localize`:@@block.rate.title:Plata în rate nu este disponibilă pentru această comandă`,
    body: $localize`:@@block.rate.body:[PLACEHOLDER] Nu este definit ce i se comunică clientului, dacă decizia se reține pe cont și pentru cât timp. Vezi cele trei variante pe S-12.`,
    actions: [
      {
        id: 'request-sales-contact',
        label: $localize`:@@block.act.requestSalesContact:Cere contact comercial`,
        primary: true,
      },
      {
        id: 'continue-sim-only',
        label: $localize`:@@block.act.continueSimOnly:Continuă doar cu abonamentul`,
      },
    ],
    // `schema.graphql` records `q: "none"` against the declined outcome — BRD
    // S-12 describes three possible treatments and assigns none. Filed under
    // Q-12 so it is counted; the three variants live on S-12 as `DeclinedOutcome`.
    outcome: unspecified<BlockOutcome>(
      'REDIRECTED',
      'Q-12',
      'Which of the three declined-instalments treatments applies, whether the decision is retained on the account, and for how long. BRD S-12 assigns no question number.',
    ),
    outcomeAssigned: false,
    degradedNote: null,
  },
};

const OUTCOME_WORD: Readonly<Record<BlockOutcome, string>> = {
  BLOCKED: $localize`:@@block.outcome.blocked:BLOCAT`,
  DEGRADED: $localize`:@@block.outcome.degraded:DEGRADAT`,
  REDIRECTED: $localize`:@@block.outcome.redirected:REDIRECȚIONAT`,
};

/**
 * Two blocks can be on one page — the S-20 catalogue renders fourteen — so the
 * heading id that `aria-labelledby` points at has to be unique per instance.
 */
let sequence = 0;

const nextHeadingId = (): string => {
  sequence += 1;
  return `orange-block-${sequence}`;
};

@Component({
  selector: 'orange-blocked-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  host: {
    // The annotation layer reads these off the DOM. The component renders nothing
    // for them, so annotations stay off until the dev toggle turns them on.
    '[attr.data-block-reason]': 'reason()',
    '[attr.data-open-question]': 'openQuestion()',
    '[attr.data-outcome-assigned]': 'trigger().outcomeAssigned',
    // Set only while a dev switch is forcing this trigger's outcome, so a
    // screenshot taken with `?block=` on can be told apart from the default.
    '[attr.data-outcome-forced]': 'forced() !== null ? forced() : null',
  },
  template: `
    <section class="block" [class]="'out-' + outcome()" [attr.aria-labelledby]="headingId">
      <p class="bar">
        <span class="mark" aria-hidden="true"></span>
        <span>{{ word() }}</span>
      </p>

      <div class="body">
        <!-- tabindex="-1" so a screen that renders this after an action can move
             focus here. Nothing else in the block is focusable when there are no
             actions, and without it the customer is told nothing. -->
        <h2 class="heading" [id]="headingId" tabindex="-1" #heading>{{ trigger().title }}</h2>
        <p class="body-text">{{ message() ?? trigger().body }}</p>

        <!-- Per trigger, not per outcome. See degradedNote on Trigger for why the
             prototype's single sentence is not repeated on all fourteen. -->
        @if (outcome() === 'DEGRADED') {
          @if (trigger().degradedNote; as note) {
            <p class="aside">{{ note }}</p>
          } @else if (degradationUndescribed()) {
            <p class="aside">
              <span class="label" i18n="@@block.aside.degradedPlaceholderLabel">[ PLACEHOLDER ]</span>
              <span i18n="@@block.aside.degradedPlaceholder">
                Nicio sursă nu descrie ce poate face clientul în continuare în acest caz.
              </span>
            </p>
          }
        }

        @if (outcome() === 'REDIRECTED') {
          <p class="aside" i18n="@@block.aside.redirected">
            Un coleg din echipa business te contactează pentru a duce comanda mai departe.
          </p>
          @if (assistedSalesPhone(); as phone) {
            <p class="phone">
              <span class="phone-label" i18n="@@block.phone.label">Vânzări business</span>
              <a class="phone-number tabular" [href]="'tel:' + phone">{{ phone }}</a>
            </p>
          }
        }

        @if (actions().length > 0) {
          <div class="actions">
            @for (action of actions(); track action.id) {
              <orange-button
                [variant]="action.primary ? 'primary' : 'secondary'"
                (clicked)="actioned.emit(action.id)"
              >
                {{ action.label }}
              </orange-button>
            }
          </div>
        }

        @if (reference(); as ref) {
          <p class="reference caption muted">
            <span i18n="@@block.reference">Referință:</span>
            <span class="tabular">PRE2POST-{{ trigger().code }}-{{ ref }}</span>
          </p>
        }
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      /* Border weight is one of the three cues. DEGRADED is not an error and gets
         the hairline every other surface in the app gets; the two that stop or
         divert the order get the 2px control border. */
      .block {
        background: var(--surface);
        border: var(--border-hairline) solid var(--grey-400);
      }

      .out-BLOCKED,
      .out-REDIRECTED {
        border: var(--border-control) solid var(--ink);
      }

      .bar {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        min-height: 28px;
        padding: var(--space-1) var(--space-3);
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
        background: var(--grey-300);
        color: var(--ink);
      }

      .out-BLOCKED .bar {
        background: var(--surface-inverse);
        color: var(--surface);
      }

      /* Orange as a fill with ink text — 7.99:1. Never as type. */
      .out-REDIRECTED .bar {
        background: var(--brand);
        color: var(--ink);
      }

      /* Third cue, after fill and word: the marker shape differs per outcome, so
         the three are distinguishable in greyscale and to a colourblind reader. */
      .mark {
        flex: 0 0 auto;
        width: 10px;
        height: 10px;
        background: currentColor;
      }

      .out-BLOCKED .mark {
        transform: rotate(45deg);
      }

      .out-REDIRECTED .mark {
        width: 0;
        height: 0;
        background: none;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 10px solid currentColor;
      }

      .body {
        padding: var(--space-3);
      }

      .body > * + * {
        margin-top: var(--space-2);
      }

      .body-text {
        font-size: 16px;
        line-height: 24px;
      }

      /* A statement of fact about the order, on a grey ground rather than a
         coloured one — it is not a second warning. */
      .aside {
        background: var(--grey-200);
        border: var(--border-hairline) solid var(--grey-400);
        padding: var(--space-2) var(--space-3);
        font-size: 14px;
        line-height: 20px;
      }

      /* The placeholder marker sits on its own line above the sentence, the same
         11px/700 label the reserved bands on S-03 and S-07 use. */
      .aside .label {
        display: block;
        margin-bottom: var(--space-1);
        color: var(--grey-700);
      }

      .phone-label {
        display: block;
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
        color: var(--grey-700);
      }

      .phone-number {
        font-size: 24px;
        line-height: 28px;
        font-weight: 700;
        color: var(--ink);
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-top: var(--space-3);
      }

      .reference {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        margin-top: var(--space-3);
        padding-top: var(--space-2);
        border-top: var(--border-hairline) solid var(--grey-400);
      }

      h2:focus-visible {
        outline: var(--border-control) solid var(--ink);
        outline-offset: 2px;
      }
    `,
  ],
})
export class BlockedStateComponent {
  readonly reason = input.required<BlockReason>();

  /**
   * The outcome the caller resolved — off the wire for the blocks the eligibility
   * chain produces, out of `errors.ts` for the ones a screen resolves itself.
   * Falls back to the table's default when absent.
   */
  readonly outcomeOverride = input<BlockOutcome | null>(null, { alias: 'outcome' });

  /** Server-supplied copy. Falls back to the trigger's placeholder text. */
  readonly message = input<string | null>(null);

  /** Rendered only for REDIRECTED. Comes from the mock BFF's config, Q-14. */
  readonly assistedSalesPhone = input<string | null>(null);

  /** Read by the annotation layer off the host attribute. Renders nothing. */
  readonly openQuestion = input<string | null>(null);

  /** OCN or order reference for the support line. Omitted when absent. */
  readonly reference = input<string | null>(null);

  readonly actioned = output<string>();

  readonly headingId = nextHeadingId();

  private readonly heading = viewChild<ElementRef<HTMLElement>>('heading');

  private readonly forcedOutcomes = inject(BlockOutcomes);

  readonly trigger = computed(() => TRIGGERS[this.reason()]);

  /** Non-null only while a dev switch is forcing this trigger. Q-12 … Q-33. */
  readonly forced = computed<BlockOutcome | null>(() =>
    this.forcedOutcomes.outcomeFor(this.reason()),
  );

  /**
   * Forced outcome first, then an *assigned* outcome, then whatever the caller
   * resolved, then the table's guess.
   *
   * Three layers, and each of the first two exists for a reason worth stating.
   *
   * The forced value wins over everything: the shell passes the wire's outcome on
   * every eligibility block, so an override that only applied when the caller
   * passed nothing would work on exactly the four triggers that need it least.
   *
   * The second layer is the awkward one. The POC has **two** independent sources
   * of default outcome and they disagree on five triggers — `errors.ts`'s
   * `presentation.outcome`, which is what arrives on the wire, and this table,
   * which is read off the prototype's fourteen-row matrix:
   *
   * | Trigger | `errors.ts` | matrix |
   * |---|---|---|
   * | `NOT_ADMINISTRATOR` | BLOCKED | REDIRECTED |
   * | `COMPANY_NOT_ACTIVE` | REDIRECTED | BLOCKED |
   * | `CUI_VALIDATION_FAILED` | REDIRECTED | BLOCKED |
   * | `NO_ELIGIBLE_OFFERS` | REDIRECTED | BLOCKED |
   * | `SUBSCRIPTION_CAP_REACHED` | REDIRECTED | **BLOCKED, assigned** |
   *
   * Four of those five are triggers where **neither** number is a source — both
   * are guesses, made twice, by two people reading the same silence (Q-12, Q-15).
   * Averaging them would manufacture a third guess, so the wire keeps winning
   * there and the divergence is recorded in `docs/SCREEN-MAP.md` instead.
   *
   * The fifth is different. `SUBSCRIPTION_CAP_REACHED` is one of only three
   * triggers whose outcome a source actually states, and letting the wire's guess
   * override it meant the POC showed REDIRECȚIONAT for the one trigger nobody has
   * to guess about. So an assigned outcome now outranks the caller: a stated fact
   * is not something a call site gets to overrule. `forced()` still sits above it,
   * because the point of the switch is to show what the alternatives would look
   * like — including for the three that are decided.
   */
  readonly outcome = computed<BlockOutcome>(() => {
    const forced = this.forced();
    if (forced !== null) return forced;
    const trigger = this.trigger();
    if (trigger.outcomeAssigned) return trigger.outcome;
    return this.outcomeOverride() ?? trigger.outcome;
  });
  /**
   * True when the block is showing DEGRADED, has no note of its own, and DEGRADED
   * is not the outcome a source assigned it — so there is a "what can they do
   * instead" question and no answer to it.
   *
   * `DEVICE_OUT_OF_STOCK` is the case this exists to exclude: assigned DEGRADED,
   * no note, and the body plus the two actions already say what continues. The
   * other thirteen only reach DEGRADED by the dev switch or by the POC's own guess,
   * and there the silence is the information.
   */
  readonly degradationUndescribed = computed(() => {
    const trigger = this.trigger();
    return !(trigger.outcomeAssigned && trigger.outcome === 'DEGRADED');
  });

  readonly word = computed(() => OUTCOME_WORD[this.outcome()]);
  readonly actions = computed<readonly BlockAction[]>(() => this.trigger().actions);

  /**
   * Called by screens that swap a section for a block in response to an action.
   * Guards that render a block on arrival do not need it — the route change moves
   * focus already.
   */
  focusHeading(): void {
    this.heading()?.nativeElement.focus();
  }
}

/** The fourteen triggers, for the dev catalogue. Not for product code. */
export const blockTriggers = (): readonly BlockReason[] =>
  Object.keys(TRIGGERS) as readonly BlockReason[];

/**
 * The short code that appears in the support reference — `FLAG`, `CAP`, `OTP`.
 *
 * Also what `?block=` takes, lowercased. They are the prototype's own trigger ids,
 * so the switch, the reference line and the S-20 catalogue all name a trigger the
 * same way, and a fourteen-character enum member does not have to go in a URL.
 */
export const blockCode = (reason: BlockReason): string => TRIGGERS[reason].code;

export const reasonForCode = (code: string): BlockReason | null => {
  const wanted = code.trim().toUpperCase();
  return blockTriggers().find((reason) => TRIGGERS[reason].code === wanted) ?? null;
};

/** Whether a source states what the customer sees. Three of fourteen. */
export const outcomeIsAssigned = (reason: BlockReason): boolean => TRIGGERS[reason].outcomeAssigned;

/** The default outcome, for the catalogue's matrix. */
export const defaultOutcome = (reason: BlockReason): BlockOutcome => TRIGGERS[reason].outcome;

/**
 * The trigger's heading, for the catalogue's matrix rows.
 *
 * Placeholder copy like everything else here — Orange supplies the real text. The
 * catalogue shows it so a reviewer reading the matrix does not have to translate
 * `CUI_SERVICE_UNAVAILABLE` in their head.
 */
export const blockTitle = (reason: BlockReason): string => TRIGGERS[reason].title;
